import { Suspense, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { globalStore } from '../global.store';
import { authorizationStore } from '../modules/authorization/authorization.store';
import { dashboardStore } from '../layout/dashboard/dashboard.store';
import { ucraftStore } from '../ucraft.store';
import useQuery from '../hooks/useQuery';
import FullScreenLoader from '../components/fullScreenLoader';
import { sectionTypes } from '../modules/authorization/authorization.type';

const AuthWrapper = (Component: any) => {
  return observer((props: any) => {
    const AuthInit = () => {
      const [authComplete, setAuthComplete] = useState(false);
      const {
        isLogin,
        setNavigation,
        projectConfiguration,
        userIsGuest,
        userData,
      } = globalStore;
      const { setAuthPopupActive, setAuthPopupType } = dashboardStore;
      const { checkUcraftToken } = ucraftStore;
      const { resetSection, clearSearchParams } = authorizationStore;

      const [searchParams, setSearchParams] = useSearchParams();
      const navigate = useNavigate();

      const authQueryParams: sectionTypes = useQuery()?.auth as sectionTypes;
      const ucraftQueryParams: string = useQuery()?.nonce;

      useEffect(() => {
        (async () => {
          await projectConfiguration();
          setNavigation(navigate);
          setAuthComplete(true);
        })();
      }, []);

      useEffect(() => {
        if (isLogin && searchParams.has('auth')) {
          setSearchParams(
            (params: any) => {
              params.delete('auth');
              return params;
            },
            { replace: true },
          );
        }
      }, [userData]);

      useEffect(() => {
        const authParamsTypes = ['login', 'registration', 'forgot'];
        if (
          authQueryParams &&
          authParamsTypes.includes(authQueryParams) &&
          userIsGuest() &&
          authComplete
        ) {
          setAuthPopupActive(true);
          setAuthPopupType(authQueryParams);
        }
      }, [authQueryParams, userData, authComplete]);

      useEffect(() => {
        if (clearSearchParams) {
          setAuthPopupActive(false);
          resetSection();
          if (!!authorizationStore.redirectUrl && isLogin) {
            navigate(authorizationStore.redirectUrl);
            authorizationStore.setRedirectUrl('');
          }
        }
      }, [clearSearchParams]);

      useEffect(() => {
        if (ucraftQueryParams) {
          checkUcraftToken(ucraftQueryParams);
          searchParams.delete('nonce');
          setSearchParams(searchParams, { replace: true });
        }
      }, [ucraftQueryParams]);

      return authComplete;
    };

    const componentAvailable = AuthInit();

    return (
      <>
        {componentAvailable ? (
          <Suspense fallback={<FullScreenLoader />}>
            <Component {...props} />
          </Suspense>
        ) : (
          <FullScreenLoader />
        )}
      </>
    );
  });
};

export default AuthWrapper;
